import { Box, Divider, Flex, Text, VStack } from '@chakra-ui/react'

import { getMetadata, useApp } from '@hb/react'
import { useCallback, useState } from 'react'
import { ActionLog } from '../ActionLog'
import { ActionButton, SolidActionButton } from '../Buttons'
import { RenderStageProps } from '../forms/renderStage/types'
import { useGynCareView } from './context'
import { MedicationPreview, SupplementPreview } from './MedicationPreview'

const MedicationsView = () => {
  const { medicationsArray, handleDeleteMedication, openMedicationConditionModal } =
    useGynCareView().conditionMedicationModal
  return (
    <>
      <Text fontWeight={500} mb={2} color="gray.600" fontFamily="Open Sans" fontSize="lg">
        Medications ({medicationsArray.length})
      </Text>
      {medicationsArray.map(medication => (
        <Box pb={2} key={medication.id}>
          <MedicationPreview
            medication={medication}
            withConditions
            onDelete={() => handleDeleteMedication('medication', medication.id)}
            onEditClick={() => openMedicationConditionModal('medication', null, medication.id)}
          />
        </Box>
      ))}
      <ActionButton
        size="sm"
        onClick={() => openMedicationConditionModal('medication', null, `${Date.now()}`)}>
        + Add Medication
      </ActionButton>
    </>
  )
}

const SupplementsView = () => {
  const { supplementsArray, handleDeleteMedication, openMedicationConditionModal } =
    useGynCareView().conditionMedicationModal
  return (
    <>
      <Text fontWeight={500} mb={2} color="gray.600" fontFamily="Open Sans" fontSize="lg">
        Supplements ({supplementsArray.length})
      </Text>
      {supplementsArray.map(supplement => (
        <Box pb={2} key={supplement.id}>
          <SupplementPreview
            supplement={supplement}
            withConditions
            onDelete={() => handleDeleteMedication('supplement', supplement.id)}
            onEditClick={() => openMedicationConditionModal('supplement', null, supplement.id)}
          />
        </Box>
      ))}
      <ActionButton
        size="sm"
        onClick={() => openMedicationConditionModal('supplement', null, `${Date.now()}`)}>
        + Add Supplement
      </ActionButton>
    </>
  )
}

export const MedsAndSupplmentsStage = ({ data }: RenderStageProps) => {
  const { updatedOn, updatedBy, updatedByGroup } = data ?? {}
  const { onSubmit, admin } = useGynCareView()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { appName } = useApp()

  const handleSubmit = useCallback(async () => {
    setSubmitting(true)
    try {
      const { error } = await onSubmit({
        medicationsAndSupplements: getMetadata(appName, !updatedOn, admin ? 'admin' : 'patient'),
      })
      if (error) setError(error)
    } catch (error: any) {
      setError(error.message)
    }
    setSubmitting(false)
  }, [onSubmit, updatedOn, admin, appName])

  return (
    <VStack
      borderRadius={4}
      align="flex-start"
      bg="gray.50"
      width="100%"
      height="100%"
      spacing={0}
      borderTop="1px solid #cdcdcd"
      shadow="md">
      <Box py={3} px={4} flex={1} minH="0" w="100%" overflowY="auto">
        <MedicationsView />
        <Divider borderColor="gray.400" mb={2} mt={3} />
        <SupplementsView />
        {updatedBy || updatedOn || updatedByGroup ? (
          <Box px={2} pt={3}>
            <ActionLog action="Updated" by={updatedBy} on={updatedOn} group={updatedByGroup} />
          </Box>
        ) : null}
      </Box>
      <Flex
        justifyContent="flex-end"
        w="100%"
        py={2}
        px={3}
        bg="#efefef"
        borderTop="1px solid #cdcdcd">
        {error ? (
          <Text color="red.600" fontFamily="Open Sans" px={3}>
            {error}
          </Text>
        ) : null}
        <SolidActionButton isLoading={submitting} onClick={handleSubmit} size="sm">
          SAVE
        </SolidActionButton>
      </Flex>
    </VStack>
  )
}
